// 🔥 인라인 가격 편집 커스텀 훅 (일괄 저장 방식)
// [배경] 테이블 전체의 인라인 편집 로직을 관리
//        엑셀처럼 다량 입력 후 한 번에 저장하는 방식
//        변경된 셀 추적, 시각적 피드백, 데이터 손실 방지

import { useCallback, useRef, useState, useEffect } from 'react';
import { GradePriceInfo, DeviceGrade } from '../../../../types/gradeSystem';
import { getUnifiedPriceService } from '../../../../services/unifiedPriceAdapter';
import { CellPosition, EditablePriceCellRef } from './EditablePriceCell';
import { generateModelId } from '../../../../types/deviceCatalog'; // 🔑 P2: + 기호 보정용

// 📌 테이블 행 데이터 인터페이스 (모델 정보 포함 - 안정화용)
export interface TableRowData {
  priceId: string;
  modelId: string;      // 🆕 모델 ID (가격 자동 생성 시 필요)
  modelName: string;
  prices: GradePriceInfo;
  rowIndex: number;
  // 🆕 가격 자동 생성 시 필요한 추가 정보
  brand?: 'Apple' | 'Samsung' | 'LG';
  deviceType?: 'smartphone' | 'tablet' | 'wearable';
  storage?: string;
  connectivity?: string;
}

// 📌 변경된 셀 정보
export interface PendingChange {
  priceId: string;
  grade: DeviceGrade;
  oldValue: number;
  newValue: number;
  modelName: string;
  timestamp: number;
}

// 📌 훅 반환 타입
export interface UseInlinePriceEditReturn {
  /** 셀 값 변경 핸들러 (로컬 상태에 저장, Firebase 저장 X) */
  handleCellChange: (position: CellPosition, newValue: number) => void;
  /** 모든 변경사항 일괄 저장 (Firebase 업데이트) */
  saveAllChanges: () => Promise<boolean>;
  /** 모든 변경사항 취소 */
  discardAllChanges: () => void;
  /** 셀 네비게이션 핸들러 */
  handleNavigate: (from: CellPosition, direction: 'next' | 'prev' | 'up' | 'down') => void;
  /** 셀 레퍼런스 등록 */
  registerCellRef: (position: CellPosition, ref: EditablePriceCellRef | null) => void;
  /** 현재 활성화된 편집 셀 위치 */
  activeCell: CellPosition | null;
  /** 활성 셀 설정 */
  setActiveCell: (position: CellPosition | null) => void;
  /** 저장 중 여부 */
  isSaving: boolean;
  /** 미저장 변경사항 목록 */
  pendingChanges: Map<string, PendingChange>;
  /** 미저장 변경사항 존재 여부 */
  hasUnsavedChanges: boolean;
  /** 변경된 셀 개수 */
  changedCellCount: number;
  /** 특정 셀이 변경되었는지 확인 */
  isCellChanged: (priceId: string, grade: DeviceGrade) => boolean;
  /** 특정 셀의 현재 값 (변경된 값 포함) */
  getCellValue: (priceId: string, grade: DeviceGrade, originalValue: number) => number;
  /** 저장 완료된 셀 목록 (애니메이션용) */
  savedCells: Set<string>;
  /** 에러 메시지 */
  error: string | null;
  /** 에러 초기화 */
  clearError: () => void;
  /** 성공 메시지 */
  successMessage: string | null;
  /** 성공 메시지 초기화 */
  clearSuccessMessage: () => void;
}

// 📌 훅 Props
export interface UseInlinePriceEditProps {
  /** 테이블 행 데이터 */
  rows: TableRowData[];
  /** 데이터 새로고침 콜백 */
  onDataRefresh?: () => Promise<void>;
  /** 저장 성공 콜백 */
  onSaveSuccess?: (changedCount: number) => void;
  /** 저장 실패 콜백 */
  onSaveError?: (error: Error) => void;
}

/**
 * 🔥 인라인 가격 편집 커스텀 훅 (일괄 저장 방식)
 * 
 * @description
 * 가격 관리 테이블의 인라인 편집 기능을 제공하는 훅입니다.
 * - 엑셀처럼 다량 입력 후 "저장 버튼"으로 일괄 저장
 * - Tab/Shift+Tab/↑/↓ 키보드 네비게이션
 * - 변경된 셀 시각적 강조 (노란색 배경)
 * - 저장 후 초록색 → 흰색 페이드아웃 애니메이션
 * - 미저장 데이터 손실 방지 (페이지 이탈 경고)
 * 
 * @example
 * const { handleCellChange, saveAllChanges, hasUnsavedChanges } = useInlinePriceEdit({
 *   rows: tableRows,
 *   onDataRefresh: () => loadData(true)
 * });
 */
export const useInlinePriceEdit = ({
  rows,
  onDataRefresh,
  onSaveSuccess,
  onSaveError
}: UseInlinePriceEditProps): UseInlinePriceEditReturn => {
  // 📊 상태 관리
  const [activeCell, setActiveCell] = useState<CellPosition | null>(null);
  const [pendingChanges, setPendingChanges] = useState<Map<string, PendingChange>>(new Map());
  const [savedCells, setSavedCells] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // 📌 셀 레퍼런스 맵 (priceId-grade → ref)
  const cellRefs = useRef<Map<string, EditablePriceCellRef>>(new Map());
  
  // 🔑 셀 키 생성 유틸리티
  const getCellKey = useCallback((priceId: string, grade: DeviceGrade): string => {
    return `${priceId}-${grade}`;
  }, []);
  
  // 📌 미저장 변경사항 존재 여부
  const hasUnsavedChanges = pendingChanges.size > 0;
  const changedCellCount = pendingChanges.size;
  
  // 🚨 페이지 이탈 시 경고 (미저장 데이터 손실 방지)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '저장하지 않은 변경사항이 있습니다. 페이지를 떠나시겠습니까?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);
  
  // 🎨 저장 완료 애니메이션 타이머 정리
  useEffect(() => {
    if (savedCells.size > 0) {
      const timer = setTimeout(() => {
        setSavedCells(new Set());
      }, 1500); // 1.5초 후 애니메이션 상태 초기화
      return () => clearTimeout(timer);
    }
  }, [savedCells]);
  
  // 📌 셀 레퍼런스 등록
  const registerCellRef = useCallback((position: CellPosition, ref: EditablePriceCellRef | null) => {
    const key = getCellKey(position.priceId, position.grade);
    if (ref) {
      cellRefs.current.set(key, ref);
    } else {
      cellRefs.current.delete(key);
    }
  }, [getCellKey]);
  
  // 🔍 특정 위치의 셀 레퍼런스 가져오기
  const getCellRef = useCallback((priceId: string, grade: DeviceGrade): EditablePriceCellRef | undefined => {
    const key = getCellKey(priceId, grade);
    return cellRefs.current.get(key);
  }, [getCellKey]);
  
  // 🔍 특정 셀이 변경되었는지 확인
  const isCellChanged = useCallback((priceId: string, grade: DeviceGrade): boolean => {
    const key = getCellKey(priceId, grade);
    return pendingChanges.has(key);
  }, [getCellKey, pendingChanges]);
  
  // 🔍 특정 셀의 현재 값 (변경된 값 포함)
  const getCellValue = useCallback((priceId: string, grade: DeviceGrade, originalValue: number): number => {
    const key = getCellKey(priceId, grade);
    const change = pendingChanges.get(key);
    return change ? change.newValue : originalValue;
  }, [getCellKey, pendingChanges]);
  
  // ✏️ 셀 값 변경 핸들러 (로컬 상태에만 저장, Firebase X)
  const handleCellChange = useCallback((position: CellPosition, newValue: number) => {
    console.log(`🔍 [handleCellChange] priceId="${position.priceId}" grade=${position.grade} value=${newValue}`);
    const cellKey = getCellKey(position.priceId, position.grade);

    // 현재 행의 원본 가격 찾기
    const currentRow = rows.find(r => r.priceId === position.priceId);
    if (!currentRow) return;
    
    const originalValue = currentRow.prices[position.grade] || 0;
    
    setPendingChanges(prev => {
      const newMap = new Map(prev);
      
      // 원본 값과 같으면 변경 목록에서 제거
      if (newValue === originalValue) {
        newMap.delete(cellKey);
      } else {
        // 변경 정보 저장
        newMap.set(cellKey, {
          priceId: position.priceId,
          grade: position.grade,
          oldValue: originalValue,
          newValue,
          modelName: currentRow.modelName,
          timestamp: Date.now()
        });
      }
      
      return newMap;
    });
  }, [rows, getCellKey]);
  
  // 💾 모든 변경사항 일괄 저장 (Firebase 업데이트) - 🔥 안정화 버전
  const saveAllChanges = useCallback(async (): Promise<boolean> => {
    if (pendingChanges.size === 0) return true;
    
    setIsSaving(true);
    setError(null);
    
    try {
      // priceId별로 변경사항 그룹화
      const changesByPriceId = new Map<string, Map<DeviceGrade, number>>();
      
      pendingChanges.forEach((change, key) => {
        if (!changesByPriceId.has(change.priceId)) {
          changesByPriceId.set(change.priceId, new Map());
        }
        changesByPriceId.get(change.priceId)!.set(change.grade, change.newValue);
      });
      
      const UnifiedPriceService = getUnifiedPriceService();
      const savedKeys: string[] = [];
      let createdCount = 0; // 🆕 자동 생성된 가격 문서 카운트
      
      // 각 priceId에 대해 업데이트 실행
      for (const [priceId, gradeChanges] of changesByPriceId) {
        // 현재 행의 전체 가격 정보 찾기
        const currentRow = rows.find(r => r.priceId === priceId);
        if (!currentRow) continue;
        
        // 기존 가격에 변경사항 적용
        const updatedPrices: GradePriceInfo = { ...currentRow.prices };
        const priceChangesLog: { grade: DeviceGrade; before: number; after: number }[] = [];
        
        gradeChanges.forEach((newValue, grade) => {
          priceChangesLog.push({
            grade,
            before: currentRow.prices[grade] || 0,
            after: newValue
          });
          updatedPrices[grade] = newValue;
          savedKeys.push(getCellKey(priceId, grade));
        });
        
        // 🔥 [디버그 로그] 가격 변경 상세 정보 (보정 전 - 위치 이동)
        console.log('\n' + '═'.repeat(80));
        console.log(`🔍 [가격수정 디버그] "${currentRow.modelName}" 저장 시작`);
        console.log('─'.repeat(80));
        console.log(`📋 모델 정보:`);
        console.log(`   ├─ modelName: "${currentRow.modelName}"`);
        console.log(`   ├─ modelId (원본 UI): "${currentRow.modelId}"`);
        console.log(`   ├─ priceId (UI): "${priceId}"`);
        console.log(`   ├─ brand: "${currentRow.brand}"`);
        console.log(`   ├─ deviceType: "${currentRow.deviceType}"`);
        console.log(`   ├─ storage: "${currentRow.storage}"`);
        console.log(`   └─ connectivity: "${currentRow.connectivity}"`);
        console.log('─'.repeat(80));
        console.log(`💰 가격 변경 내역:`);
        priceChangesLog.forEach(({ grade, before, after }) => {
          console.log(`   ├─ ${grade}등급: ${before.toLocaleString()}원 → ${after.toLocaleString()}원 (${after - before > 0 ? '+' : ''}${(after - before).toLocaleString()}원)`);
        });
        console.log('─'.repeat(80));
        
        // 🔥 안전한 저장: 모델 정보가 있으면 safeUpdatePrice 사용
        if (currentRow.modelId && currentRow.brand && currentRow.deviceType) {
          // 🔑 보정 로직: modelId의 brand와 currentRow.brand가 다른 경우 + 기호 누락 케이스
          let correctedModelId = currentRow.modelId;
          const modelName = currentRow.modelName || '';
          const idLower = currentRow.modelId.toLowerCase();

          // 🆕 1. Brand 불일치 감지 (제조사 변경 후 페이지가 새로고침되지 않은 경우)
          const modelIdBrand = currentRow.modelId.split('-')[0]; // 예: "samsung-..." → "samsung"
          const currentBrand = currentRow.brand.toLowerCase(); // 예: "LG" → "lg"

          if (modelIdBrand !== currentBrand) {
            // 🚨 심각한 불일치: 이 모델은 더 이상 존재하지 않음 (제조사 변경으로 삭제+재생성됨)
            const errorMsg =
              `⚠️ 모델이 이동되었습니다!\n\n` +
              `모델 "${modelName}"의 제조사가 다른 관리자에 의해 변경되었습니다.\n\n` +
              `변경 내역:\n` +
              `• 이전 ID: "${currentRow.modelId}" (${modelIdBrand})\n` +
              `• 새 제조사: "${currentRow.brand}"\n\n` +
              `📌 조치 방법:\n` +
              `1. 페이지를 새로고침하세요 (F5 또는 Ctrl+R)\n` +
              `2. "${currentRow.brand}" 메뉴로 이동하여 모델을 확인하세요\n` +
              `3. 새로운 모델ID로 가격을 수정할 수 있습니다\n\n` +
              `참고: 제조사 변경 시 모델은 삭제 후 새 ID로 재생성됩니다.`;

            console.error('❌ [Brand 불일치 - 모델 이동됨]', {
              modelId: currentRow.modelId,
              modelIdBrand,
              newBrand: currentBrand,
              modelName,
              reason: '제조사 변경으로 인한 모델 재생성'
            });

            // 사용자에게 에러 표시
            throw new Error(errorMsg);
          }

          // 🔑 2. P2 핵심: modelId가 + 기호를 누락한 경우 modelName에서 복원
          // (brand 보정이 이미 되었으면 correctedModelId 사용)
          const hasPlusPlus = modelName.includes('++') || modelName.includes('플러스플러스');
          const hasPlus = !hasPlusPlus && (modelName.includes('+') || modelName.includes('플러스'));

          // ID에 이미 올바르게 반영되어 있는지 확인
          const correctedIdLower = correctedModelId.toLowerCase();
          const idHasPlusPlus = correctedIdLower.includes('plus-plus');
          const idHasPlus = !idHasPlusPlus && correctedIdLower.includes('-plus');

          // 보정이 필요한 경우 (모델명에는 +가 있지만 ID에는 없음)
          if ((hasPlusPlus && !idHasPlusPlus) || (hasPlus && !idHasPlus)) {
            // 모델명에서 series 추출 (예: "Galaxy S25++" → "Galaxy S")
            const seriesMatch = modelName.match(/^(Galaxy\s*[A-Za-z]+)/i) ||
                               modelName.match(/^(갤럭시\s*[가-힣A-Za-z]+)/i) ||
                               modelName.match(/^(iPhone)/i) ||
                               modelName.match(/^(iPad)/i);
            const series = seriesMatch ? seriesMatch[1].trim() : 'Unknown';

            correctedModelId = generateModelId({
              brand: currentRow.brand,
              deviceType: currentRow.deviceType,
              series: series,
              modelEn: modelName
            });

            console.log(`🔧 [P2 + 기호 보정] "${modelName}": "${currentRow.modelId}" → "${correctedModelId}"`);
          }
          
          const result = await UnifiedPriceService.safeUpdatePrice(
            {
              priceId,
              gradePrices: updatedPrices,
              memo: `일괄 편집: ${gradeChanges.size}개 등급 수정 - ${new Date().toLocaleString()}`
            },
            {
              modelId: correctedModelId,  // 🔑 P2: 보정된 modelId 사용
              brand: currentRow.brand,
              deviceType: currentRow.deviceType,
              storage: currentRow.storage || '기본',
              connectivity: currentRow.connectivity
            },
            'admin'
          );
          
          // 🔥 [디버그 로그] 저장 결과
          const wasModelIdCorrected = correctedModelId !== currentRow.modelId;
          console.log(`📝 저장 결과:`);
          console.log(`   ├─ modelId 보정: ${wasModelIdCorrected ? `✅ "${currentRow.modelId}" → "${correctedModelId}"` : '불필요'}`);
          console.log(`   ├─ canonicalPriceId (DB): "${result.canonicalPriceId}"`);
          console.log(`   ├─ priceId 일치여부: ${priceId === result.canonicalPriceId ? '✅ 일치' : '⚠️ 불일치!'}`);
          if (priceId !== result.canonicalPriceId) {
            console.log(`   │  ⚠️ UI priceId: "${priceId}"`);
            console.log(`   │  ⚠️ DB priceId: "${result.canonicalPriceId}"`);
            console.log(`   │  ⚠️ 불일치 원인: READ/WRITE 경로가 다른 문서를 참조!`);
          }
          console.log(`   ├─ 신규 생성: ${result.created ? '예' : '아니오'}`);
          console.log(`   └─ 업데이트됨: ${result.updated ? '예' : '아니오'}`);
          console.log('═'.repeat(80) + '\n');
          
          if (result.created) {
            createdCount++;
          }
        } else {
          // 모델 정보가 없으면 기존 방식으로 업데이트 (폴백)
          // 먼저 존재 여부 확인
          const exists = await UnifiedPriceService.checkPriceExists(priceId);
          
          if (!exists) continue;
          
          await UnifiedPriceService.updatePrice({
            priceId,
            gradePrices: updatedPrices,
            memo: `일괄 편집: ${gradeChanges.size}개 등급 수정 - ${new Date().toLocaleString()}`
          }, 'admin');
        }
        
      }
      
      // 저장 완료된 셀 표시 (애니메이션용)
      setSavedCells(new Set(savedKeys));
      
      // 변경사항 초기화
      setPendingChanges(new Map());
      
      // 성공 메시지 (자동 생성 정보 포함)
      const totalChanges = pendingChanges.size;
      const message = createdCount > 0 
        ? `${totalChanges}개 가격 저장됨 (${createdCount}개 신규 생성)`
        : `${totalChanges}개 가격이 성공적으로 저장되었습니다.`;
      setSuccessMessage(message);
      
      // 성공 콜백
      onSaveSuccess?.(totalChanges);
      
      // 데이터 새로고침
      if (onDataRefresh) {
        await onDataRefresh();
      }
      
      console.log(`✅ 가격 저장 완료: ${totalChanges}개`);
      return true;
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '알 수 없는 오류';
      console.error('❌ 일괄 저장 실패:', err);
      
      setError(`저장 실패: ${errorMessage}`);
      onSaveError?.(err instanceof Error ? err : new Error(errorMessage));
      
      return false;
      
    } finally {
      setIsSaving(false);
    }
  }, [pendingChanges, rows, getCellKey, onDataRefresh, onSaveSuccess, onSaveError]);
  
  // ❌ 모든 변경사항 취소
  const discardAllChanges = useCallback(() => {
    if (pendingChanges.size === 0) return;
    
    const count = pendingChanges.size;
    setPendingChanges(new Map());
    setSuccessMessage(`${count}개 변경사항이 취소되었습니다.`);
  }, [pendingChanges]);
  
  // 🧭 셀 네비게이션 핸들러
  const handleNavigate = useCallback((from: CellPosition, direction: 'next' | 'prev' | 'up' | 'down') => {
    const grades: DeviceGrade[] = ['A', 'B', 'C', 'D', 'E']; // navigation uses all grades since data always has E
    const currentGradeIndex = grades.indexOf(from.grade);
    const currentRowIndex = from.rowIndex;
    
    let targetPriceId = from.priceId;
    let targetGrade = from.grade;
    let targetRowIndex = currentRowIndex;
    
    switch (direction) {
      case 'next':
        // Tab: 다음 등급으로 이동, 마지막 등급이면 다음 행의 A등급
        if (currentGradeIndex < 4) {
          // 같은 행의 다음 등급
          targetGrade = grades[currentGradeIndex + 1];
        } else {
          // 다음 행의 A등급
          if (currentRowIndex < rows.length - 1) {
            targetRowIndex = currentRowIndex + 1;
            targetPriceId = rows[targetRowIndex].priceId;
            targetGrade = 'A';
          } else {
            // 마지막 행의 마지막 등급이면 이동 없음
            return;
          }
        }
        break;
        
      case 'prev':
        // Shift+Tab: 이전 등급으로 이동, 첫 등급이면 이전 행의 E등급
        if (currentGradeIndex > 0) {
          // 같은 행의 이전 등급
          targetGrade = grades[currentGradeIndex - 1];
        } else {
          // 이전 행의 E등급
          if (currentRowIndex > 0) {
            targetRowIndex = currentRowIndex - 1;
            targetPriceId = rows[targetRowIndex].priceId;
            targetGrade = 'E';
          } else {
            // 첫 행의 첫 등급이면 이동 없음
            return;
          }
        }
        break;
        
      case 'up':
        // ↑: 같은 등급의 이전 행
        if (currentRowIndex > 0) {
          targetRowIndex = currentRowIndex - 1;
          targetPriceId = rows[targetRowIndex].priceId;
        } else {
          // 첫 행이면 이동 없음
          return;
        }
        break;
        
      case 'down':
        // ↓: 같은 등급의 다음 행
        if (currentRowIndex < rows.length - 1) {
          targetRowIndex = currentRowIndex + 1;
          targetPriceId = rows[targetRowIndex].priceId;
        } else {
          // 마지막 행이면 이동 없음
          return;
        }
        break;
    }
    
    // 타겟 셀 찾아서 편집 모드 시작
    const targetRef = getCellRef(targetPriceId, targetGrade);
    if (targetRef) {
      // 활성 셀 업데이트
      setActiveCell({
        priceId: targetPriceId,
        grade: targetGrade,
        rowIndex: targetRowIndex,
        colIndex: grades.indexOf(targetGrade)
      });
      
      // 타겟 셀 편집 모드 시작
      targetRef.focus();
    }
  }, [rows, getCellRef]);
  
  // 🔄 에러 초기화
  const clearError = useCallback(() => {
    setError(null);
  }, []);
  
  // 🔄 성공 메시지 초기화
  const clearSuccessMessage = useCallback(() => {
    setSuccessMessage(null);
  }, []);
  
  return {
    handleCellChange,
    saveAllChanges,
    discardAllChanges,
    handleNavigate,
    registerCellRef,
    activeCell,
    setActiveCell,
    isSaving,
    pendingChanges,
    hasUnsavedChanges,
    changedCellCount,
    isCellChanged,
    getCellValue,
    savedCells,
    error,
    clearError,
    successMessage,
    clearSuccessMessage
  };
};

export default useInlinePriceEdit;
